/**
 * The admin panel's small UI kit. Every page draws its chrome from here so
 * the panel reads as one product and matches the tenant app's canon
 * (`lib/styleKit`): 24px/590 page title, 13px body, 11px/590 uppercase
 * eyebrows, soft-tint pills, hairline borders, rounded-lg surfaces.
 */
import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'
import {
  cardChromeClass,
  cardTitleStyle,
  pillActiveClass,
  pillBaseClass,
  pillInactiveClass,
  pillTextStyle,
  sectionEyebrowClass,
  sectionEyebrowStyle,
} from '@/lib/styleKit'
import { formatDate } from '../lib/labels'
import type { ActivityEntry } from '../types'
import { groupActivity } from '../lib/labels'

export const titleStyle = { fontSize: '24px', fontWeight: 590, letterSpacing: '-0.288px', lineHeight: 1.33 } as const
export const bodyStyle = { fontSize: '13px', letterSpacing: '-0.13px', lineHeight: 1.5 } as const
export const metaStyle = { fontSize: '12px', letterSpacing: '-0.05px' } as const

export function PageHeader({ title, description, actions }: { title: string; description?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 flex-wrap">
      <div className="min-w-0">
        <h1 className="text-foreground dark:text-[#f7f8f8]" style={titleStyle}>
          {title}
        </h1>
        {description && (
          <p className="text-muted-foreground dark:text-[#8a8f98] mt-1" style={bodyStyle}>
            {description}
          </p>
        )}
      </div>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </div>
  )
}

export function Panel({ className, children }: { className?: string; children: ReactNode }) {
  return <section className={cn('border bg-background', cardChromeClass, className)}>{children}</section>
}

export function PanelHeader({ title, aside }: { title: ReactNode; aside?: ReactNode }) {
  return (
    <header className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border/50 dark:border-white/[0.06]">
      <h2 className="text-foreground dark:text-[#f7f8f8]" style={cardTitleStyle}>
        {title}
      </h2>
      {aside && <div className="text-muted-foreground dark:text-[#8a8f98]" style={metaStyle}>{aside}</div>}
    </header>
  )
}

export function Eyebrow({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span className={cn(sectionEyebrowClass, className)} style={sectionEyebrowStyle}>
      {children}
    </span>
  )
}

/** Label/value pair for detail panels. */
export function Field({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  return (
    <div className={className}>
      <Eyebrow>{label}</Eyebrow>
      <div className="mt-1 text-foreground dark:text-[#f7f8f8] break-words" style={bodyStyle}>
        {children}
      </div>
    </div>
  )
}

export type Tone = 'neutral' | 'info' | 'warn' | 'danger' | 'success' | 'brand' | 'violet'

const TONE_CLASS: Record<Tone, string> = {
  neutral: 'bg-muted/70 text-muted-foreground dark:bg-white/[0.06] dark:text-[#8a8f98]',
  info: 'bg-sky-500/10 text-sky-700 dark:bg-sky-400/10 dark:text-sky-300',
  warn: 'bg-amber-500/10 text-amber-700 dark:bg-amber-400/10 dark:text-amber-300',
  danger: 'bg-red-500/10 text-red-700 dark:bg-red-400/10 dark:text-red-300',
  success: 'bg-emerald-500/10 text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-300',
  brand: 'bg-[#5e6ad2]/10 text-[#5e6ad2] dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc]',
  violet: 'bg-violet-500/10 text-violet-700 dark:bg-violet-400/10 dark:text-violet-300',
}

export function Badge({ tone = 'neutral', className, children }: { tone?: Tone; className?: string; children: ReactNode }) {
  return (
    <span
      className={cn('inline-flex items-center gap-1 rounded-md px-1.5 h-5 whitespace-nowrap', TONE_CLASS[tone], className)}
      style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.05px' }}
    >
      {children}
    </span>
  )
}

export const BUG_STATUS_TONE: Record<string, Tone> = { todo: 'neutral', in_progress: 'warn', resolved: 'success' }
export const SUGGESTION_STATUS_TONE: Record<string, Tone> = {
  new: 'neutral',
  under_review: 'info',
  planned: 'violet',
  done: 'success',
  declined: 'danger',
}
export const SEVERITY_TONE: Record<string, Tone> = { Low: 'neutral', Medium: 'info', High: 'warn', Blocking: 'danger' }
export const IMPORTANCE_TONE: Record<string, Tone> = { 'Nice to have': 'neutral', Important: 'warn', Critical: 'danger' }

/** Filter pill with an optional count; soft indigo when selected (§48). */
export function FilterPill({
  active,
  onClick,
  count,
  children,
}: {
  active: boolean
  onClick: () => void
  count?: number
  children: ReactNode
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(pillBaseClass, active ? pillActiveClass : pillInactiveClass)}
      style={pillTextStyle}
    >
      {children}
      {count != null && (
        <span className={cn('rounded-full px-1.5 tabular-nums', active ? 'bg-[#5e6ad2]/15' : 'bg-muted/70 dark:bg-white/[0.06]')}>
          {count}
        </span>
      )}
    </button>
  )
}

export const thClass = 'px-3 py-2.5 text-left align-middle text-muted-foreground dark:text-[#8a8f98] font-normal whitespace-nowrap'
export const thStyle = { fontSize: '11px', fontWeight: 590, letterSpacing: '0.06em', textTransform: 'uppercase' } as const
export const tdClass = 'px-3 py-2.5 align-middle'
export const rowClass = 'hover:bg-muted/40 dark:hover:bg-white/[0.03] transition-colors duration-100'
export const tbodyClass = 'divide-y divide-border/50 dark:divide-white/[0.06]'

export function EmptyState({ icon, children }: { icon?: ReactNode; children: ReactNode }) {
  return (
    <div className="py-14 text-center">
      {icon && <div className="mx-auto mb-2 text-muted-foreground/40">{icon}</div>}
      <p className="text-muted-foreground dark:text-[#8a8f98]" style={bodyStyle}>
        {children}
      </p>
    </div>
  )
}

export function ErrorBanner({ children }: { children: ReactNode }) {
  return (
    <p
      role="alert"
      className="rounded-md border border-red-500/30 bg-red-500/[0.06] px-3 py-2 text-red-700 dark:text-red-300"
      style={bodyStyle}
    >
      {children}
    </p>
  )
}

/**
 * Activity log. One save that changes several fields writes one entry per
 * field with the same actor and timestamp; those are shown as ONE event
 * with its changes listed, so "who · when" never repeats.
 */
export function ActivityLog({ entries, statusLabel }: { entries: ActivityEntry[]; statusLabel?: (s: string) => string }) {
  const events = groupActivity(entries, statusLabel)
  if (events.length === 0) {
    return (
      <p className="text-muted-foreground dark:text-[#8a8f98]" style={metaStyle}>
        No changes yet.
      </p>
    )
  }
  return (
    <ol className="relative space-y-3 pl-4 before:absolute before:left-[5px] before:top-1.5 before:bottom-1.5 before:w-px before:bg-border/60 dark:before:bg-white/[0.08]">
      {events.map((ev) => (
        <li key={ev.key} className="relative">
          <span className="absolute -left-4 top-1.5 h-[7px] w-[7px] rounded-full bg-[#5e6ad2]/70" aria-hidden="true" />
          <p className="text-muted-foreground dark:text-[#8a8f98]" style={metaStyle}>
            <span className="text-foreground dark:text-[#f7f8f8]">{ev.actor}</span>
            {' · '}
            {formatDate(ev.at)}
          </p>
          <ul className="mt-0.5 space-y-0.5">
            {ev.changes.map((c, i) => (
              <li key={i} className="text-foreground dark:text-[#f7f8f8]" style={bodyStyle}>
                {c}
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ol>
  )
}
